import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PencilIcon, PlusIcon } from '@/components/catalog/icons';
import { B2bServiceMapFormModal } from '@/components/b2b-service-maps/B2bServiceMapFormModal';
import {
  DELETE_CONSEQUENCE,
  productSubtitle,
} from '@/components/b2b-service-maps/b2b-service-map-rules';
import { useB2bServiceProducts } from '@/components/b2b-service-maps/use-b2b-service-products';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { B2bServiceMap, Product } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * B2B service maps — the look-up table a proposal's service keys resolve
 * against.
 *
 * ── 🔴 WHY THIS SCREEN EXISTS ─────────────────────────────────────────────
 *
 * The B2B site sends the services an institution ticked as ITS OWN product
 * slugs — strings from another repository's `d_products.product_slug`, a column
 * EMERGE's own catalogue does not have. `f_b2b_proposal_service_map` is the one
 * place those two vocabularies meet, it is deployed, and **it is EMPTY**:
 * migration 231 seeds nothing, deliberately, because the slugs belong to another
 * repository and one of them is not fixed yet. Until somebody fills it, **every
 * service on every proposal resolves to nothing** and reads as the raw key.
 *
 * ⛔ **Before this page there was no way for a person to put a row in it at
 * all** — no screen, no form, only SQL against production.
 *
 * ── 🔴 AND AN UNMAPPED KEY COSTS NO ENQUIRY, WHICH CHANGES EVERY SENTENCE ──
 *
 * This is the same SHAPE as `/event-maps` and **not the same stakes**, and the
 * difference is the first thing to read before borrowing any of its copy. There,
 * a key resolving to nothing is a **422 at submission time** — an unmapped key is
 * a customer pressing Send and being refused, which is why that screen's empty
 * state is worded as a consequence and its nav row is filed as urgent. Here the
 * owner ruled the opposite (contract §4.3): **a proposal is never refused because
 * a mapping row is missing.** The key is stored verbatim, the join is LEFT, and
 * the service comes back with `name: null` — a legal, ordinary, day-one state.
 *
 * ⛔ **So nothing on this page may be worded as a fault, a warning or a
 * countdown**, and no empty state may imply the mappings are on their way.
 * Nothing seeds this table; a row exists because an administrator entered one.
 * What filling it buys is LEGIBILITY — a proposal reading "Student Recruitment"
 * instead of `student-recruitment` — and that is worth saying plainly and worth
 * nothing more than that.
 *
 * ── ⚠️ IT IS A CONFIGURATION SCREEN, NOT A DAILY ONE ──────────────────────
 *
 * Somebody sits down ONCE with a list of slugs from the B2B repository and
 * matches them to products. Everything here is shaped by that and not by the
 * habits of a list page:
 *
 *  - **No pager, no search, no status filter.** The job is *"is every key
 *    mapped, and mapped to the right thing"* — a question about COMPLETENESS
 *    that page 1 of N cannot answer, because a missing mapping and a mapping on
 *    the next page look identical. The list is read whole or it refuses.
 *  - ⛔ **AND THE SCREEN STILL CANNOT TELL YOU A KEY IS MISSING — the reader
 *    brings that list.** An unmapped key has **no row here at all**: the
 *    unmapped state is an ABSENT ROW, not a row pointing nowhere, which is why
 *    `product` is `NOT NULL`. So completeness is checked by reading this table
 *    against the sender's own list of slugs, and reading it whole is what makes
 *    that possible rather than what performs it. ⚠️ **Do not word anything here
 *    as though the screen knew which keys exist**, and do not reach for a "not
 *    mapped" row: `B2bProposalSubmission.services` is where an unresolved key
 *    surfaces, with `name: null`.
 *  - ⛔ **Retired mappings are never hidden by default.** A mapping an
 *    administrator has stopped offering is exactly what somebody is hunting, and
 *    a filter nobody set would remove it silently in the direction of a shorter
 *    list that reads as a tidy configuration.
 *  - ⛔ **SEVERAL KEYS MAY POINT AT ONE PRODUCT, AND THAT IS THE DESIGN**, so
 *    the rows are rendered exactly as they arrive. A form is rewritten or a
 *    service renamed while it is the same service, and both keys must keep
 *    resolving — the old one because stored proposals already carry it.
 *    **`product` is not a second identity for a row**; nothing here may group or
 *    de-duplicate by it.
 *  - **ONE table, so the page holds it rather than a panel component.**
 *    `/event-maps` splits into two `EventMapPanel`s because nineteen city
 *    mappings failing to load must not take seven festival mappings off the
 *    screen — two reads that must fail independently. There is one read here, so
 *    a component would have one caller and constant props, and the split would be
 *    the precedent's SHAPE without its reason.
 *
 * ── 🔴 THE FAILURE EVERY DECISION HERE IS ABOUT ───────────────────────────
 *
 * **A mapping that points at the WRONG product is accepted cleanly and is
 * indistinguishable afterwards.** It resolves an institution's enquiry to a
 * product they did not ask about, on every screen, for ever — and the raw key is
 * joined to only through this table, so no artefact anywhere disagrees with it.
 * The catalogue's names share long prefixes and differ at the TAIL, which is
 * precisely what a picker's trigger truncates away.
 *
 * So a product's CODE and its add-on and retirement markers are rendered
 * everywhere a person could be about to be wrong: in the picker's searchable
 * hint, under the picker once something is chosen, and in the table cell. That
 * costs a read of the product catalogue on a screen that already has the
 * mappings, and it is the point of the screen.
 *
 * ── Who can open it ───────────────────────────────────────────────────────
 *
 * ADMIN ONLY. The nav row is `adminOnly` with **no `module` slug** — there is no
 * `b2b-service-maps` permission in the catalogue and this app must not invent
 * one: a slug is a join key against somebody else's table, and an invented one
 * resolves to nothing, which **DEGRADES OPEN** and hands the row back to
 * everybody it exists to keep out.
 *
 * ⚠️ **THE ROUTE CARRIES NO GUARD**, on this app's standing rule: a hidden row is
 * an optimisation and the server's refusal is the guarantee, so somebody arriving
 * by URL meets the page's own refusal rather than a redirect or a blank screen.
 * **No request is issued at all for a reader who cannot be served** — an empty
 * table would read as "nothing is mapped", which is a different statement and the
 * one this screen exists to make truthfully.
 *
 * A real 403 mid-session is still handled below: a role can change under a live
 * access token and that token lasts days.
 */

const SKELETON_ROWS = 3;

/**
 * The table's floor, in px.
 *
 * 🔴 **AN INLINE STYLE, NEVER A CLASS.** Tailwind v4 scans SOURCE TEXT, so a
 * computed `min-w-[…]` emits no rule at all — the class lands in the DOM and does
 * nothing, which looks exactly like a table that chose to have no floor.
 *
 * ⚠️ **Each term is the pixel value of the header class beside it**, written out
 * together so the arithmetic cannot drift from the rendering. `w-28` is
 * `28 × 0.25rem` = 112px. Key and "Maps to" are the fluid columns and carry no
 * `w-*`, so they contribute floors rather than widths.
 */
const KEY_COLUMN_FLOOR = 190;
const PRODUCT_COLUMN_FLOOR = 260;
const TABLE_MIN_WIDTH =
  KEY_COLUMN_FLOOR + // Service key  (fluid)
  PRODUCT_COLUMN_FLOOR + // Maps to  (fluid)
  112 + // Status   w-28
  112; // Actions  w-28

const TABLE_STYLE = { minWidth: `${TABLE_MIN_WIDTH}px` };

const LOAD_FAILED = 'Couldn’t load the service mappings.';

export function B2bServiceMapsPage() {
  const { ability } = useAuth();

  /* ── Not an administrator: explain, and never fetch ──────────────────── */

  /*
   * 🔴 2026-10-01: the backend gates every route here on the `settings` module
   * (`requireGrant('settings', …)` in `b2b-service-map.routes.ts`), and the set
   * decides for every role. *SUPERSEDED: `!ability.isAdmin`.*
   * 2026-10-02 (owner): the maps are their own module, `settings-maps`.
   */
  if (!ability.canRead('settings-maps')) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B proposals" title="Service maps" />
        <Panel aria-labelledby="b2b-service-maps-denied-heading">
          <PanelHeader id="b2b-service-maps-denied-heading" title="Service maps" />
          <EmptyState
            icon={<TagIcon />}
            title="Not available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  return <B2bServiceMapsTable />;
}

/*
 * ⚠️ **A SEPARATE COMPONENT SO THE HOOKS BELOW NEVER RUN FOR A DENIED READER.**
 * The refusal above returns before this mounts, so neither the mappings nor the
 * catalogue is fetched — which is the "issue no request" half of the rule stated
 * in the header, and it cannot be done with an early return inside one component
 * without breaking the rules of hooks.
 */
function B2bServiceMapsTable() {
  const [rows, setRows] = useState<B2bServiceMap[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** A 403 is a role change under a live token, not a validation failure. */
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<B2bServiceMap | null>(null);
  const [pendingDelete, setPendingDelete] = useState<B2bServiceMap | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * The products behind the `{id, name}` on each row. ⚠️ **Not a nicety: the
   * mapping row carries no code and no markers**, and the catalogue's names share
   * long prefixes, so without this the table prints two near-identical products
   * under one truncated word and cannot be used to check a mapping at all.
   */
  const products = useB2bServiceProducts();

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    return api
      .listB2bServiceMaps(signal)
      /*
       * ⚠️ **`Envelope<B2bServiceMap[]>`, NOT `Paginated`** — the endpoint has no
       * pager, so there is no `meta` and nothing to read a total or a page count
       * from. That is the `listUserGroups` shape and it is the wire half of this
       * screen's read-whole rule: there is no second page to be missing, which is
       * exactly what makes "is every key mapped" answerable here.
       */
      .then(({ data }) => {
        setRows(data);
        setError(null);
        setForbidden(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          /*
           * The token is still valid and the role behind it no longer reaches
           * this table. No `details` on a 403, so there is nothing to hang on a
           * field — the whole surface goes with a banner saying why.
           */
          setForbidden(
            caught.message || 'Your account no longer has permission to see these mappings.',
          );
          setError(null);
          setRows(null);
          return;
        }
        setForbidden(null);
        setError(caught instanceof Error ? caught.message : LOAD_FAILED);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const initialLoad = loading && rows === null;
  const list = useMemo(() => rows ?? [], [rows]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteB2bServiceMap(pendingDelete.id);
      setPendingDelete(null);
      /*
       * Re-read rather than dropping the row locally. The list is read whole, so
       * a re-read is one request and it is the only thing that can confirm the
       * row is actually gone.
       */
      await load();
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Verbatim — a row already gone, or a refusal, comes back naming what
        // happened, and the server's sentence beats anything invented here.
        setDeleteError(caught.message);
      } else {
        setDeleteError('Could not reach the server. The mapping has NOT been removed.');
      }
    } finally {
      setDeleting(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  /*
   * ⛔ A FAILED READ SAYS NOTHING. IT MUST NEVER SAY "0 service mappings".
   *
   * `rows === null` means WE COULD NOT ASK; `rows === []` means we asked and
   * there are none. `list` is `rows ?? []`, so counting `list` collapses those
   * two into the same sentence — and on THIS screen that sentence is the whole
   * point. A count of zero over a failed read is a completeness claim made
   * about a list nobody managed to fetch.
   *
   * ⚠️ IT WAS ALSO ANNOUNCED. `countLabel` feeds a `role="status"` region as
   * well as the visible description, so a screen reader was told "0 service
   * mappings" and then, separately, that the list could not be read.
   *
   * 🔴 FOUND BY DRIVING IT, 2026-09-20 — on a mid-session 403 and on a 500, the
   * header read "0 service mappings" 31px above the banner saying the read had
   * failed. Neither `tsc` nor a lint could see it: both branches are strings.
   *
   * ⚠️ The empty state below already handles this correctly, which is what makes
   * this a GAP rather than a decision — it switches to "Nothing to show" and
   * withholds its create action on a failed read. The count did not get the same
   * treatment, and the two sit in one panel.
   *
   * `null` rather than a dash or "Unknown": the failure already has a sentence
   * of its own directly beneath, and a second phrasing of the same fact invites
   * a reader to take them for two different facts.
   */
  const countLabel =
    rows === null
      ? loading
        ? 'Loading…'
        : null
      : `${list.length} ${list.length === 1 ? 'service mapping' : 'service mappings'}`;

  function renderActions(row: B2bServiceMap): ReactNode {
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<PencilIcon />}
          aria-label={`Edit the mapping for ${row.serviceKey}`}
          title={`Edit the mapping for ${row.serviceKey}`}
          onClick={() => {
            setEditing(row);
            setFormOpen(true);
          }}
        />
        <Button
          variant="danger"
          size="sm"
          onClick={() => {
            setDeleteError(null);
            setPendingDelete(row);
          }}
        >
          Delete
        </Button>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      {/*
        * ⚠️ **ONE `<h1>`, from `PageHeader`**, with the panel's heading below it —
        * `AppShell` renders the `<main>`, so this page adds none of its own.
        */}
      <PageHeader eyebrow="B2B proposals" title="Service maps" />

      <Panel className="min-w-0" aria-labelledby="b2b-service-maps-heading">
        <PanelHeader
          id="b2b-service-maps-heading"
          title="Service keys"
          description={countLabel}
          actions={
            <Button variant="primary" size="sm" leadingIcon={<PlusIcon />} onClick={openCreate}>
              Map a service key
            </Button>
          }
        />

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {forbidden && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4"
            title="Your account can no longer see these mappings."
          >
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">
              {forbidden} Sign out and back in to pick up the change.
            </p>
          </Notice>
        )}

        {error && (
          <Notice tone="danger" role="alert" className="m-4" title={LOAD_FAILED}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {/*
          * ⚠️ **A SEPARATE, QUIETER NOTICE FOR THE CATALOGUE READ, AND IT IS NOT
          * AN ERROR.** The mappings themselves loaded; what failed is the list
          * this screen joins to for a product's code and markers. The rows below
          * are still correct and still worth reading — they simply cannot be
          * checked against the catalogue — so wording this as a failure would
          * send somebody hunting a fault in data that is fine.
          */}
        {!forbidden && products.error && (
          <Notice tone="neutral" className="mx-4 my-2">
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Each mapping&rsquo;s product name is shown, but the catalogue behind it could not be
              read, so two similar products cannot be told apart here. {products.error}
            </span>
          </Notice>
        )}

        {!forbidden && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && rows !== null && 'opacity-60',
            )}
          >
            {initialLoad ? (
              <TableWrap frozenHeader>
                <Table style={TABLE_STYLE}>
                  <MapTableHead />
                  <TBody>
                    {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                      <TR key={i}>
                        <TD>
                          <Skeleton className="h-3 w-32" />
                        </TD>
                        <TD>
                          <div className="grid gap-1.5">
                            <Skeleton className="h-3 w-48" />
                            <Skeleton className="h-2.5 w-32" />
                          </div>
                        </TD>
                        <TD>
                          <Skeleton className="h-4 w-16" />
                        </TD>
                        <TD numeric>
                          <div className="flex justify-end gap-1.5">
                            <Skeleton className="h-6 w-6" />
                            <Skeleton className="h-6 w-14" />
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            ) : list.length === 0 ? (
              <EmptyState
                icon={<TagIcon />}
                title={error ? 'Nothing to show' : 'No service keys are mapped'}
                /*
                 * ⛔ **THE EMPTY STATE NAMES WHAT IS *NOT* AT STAKE, AND IT MUST
                 * NOT BORROW `/event-maps`' URGENCY.** Nothing seeds this table,
                 * so empty is the day-one state — and by the owner's ruling it
                 * costs no proposal: an unmapped key is stored, kept verbatim and
                 * reported unresolved, never refused. Saying so is the whole job
                 * of this sentence, because a reader meeting an empty
                 * configuration table will assume something is broken.
                 *
                 * ⛔ And it must not imply rows are coming. A mapping exists
                 * because an administrator entered one.
                 */
                description={
                  error
                    ? 'Couldn’t load the list — see the message above.'
                    : 'Proposals still arrive and keep every service they named. Those services read as the sender’s own key until a mapping here gives them a product name.'
                }
                action={
                  error ? undefined : (
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<PlusIcon />}
                      onClick={openCreate}
                    >
                      Map a service key
                    </Button>
                  )
                }
              />
            ) : (
              <TableWrap frozenHeader>
                <Table style={TABLE_STYLE}>
                  <MapTableHead />
                  <TBody>
                    {list.map((row) => (
                      <TR key={row.id} interactive>
                        <TD>
                          {/*
                            * ⚠️ **A `<code>`, and rendered EXACTLY as stored.**
                            * These are the B2B site's own strings — hyphens and
                            * underscores — and this app never normalises,
                            * case-folds or rewrites one. A proportional font
                            * hides the difference between an underscore and a
                            * hyphen, which is the whole of what makes a key
                            * wrong.
                            */}
                          <code className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
                            {row.serviceKey}
                          </code>
                        </TD>
                        <TD>
                          <ProductCell
                            row={row}
                            record={products.byId.get(row.product.id) ?? null}
                          />
                        </TD>
                        <TD>
                          {/*
                            * ⚠️ **`Offered` / `Not offered`, never
                            * `Active` / `Inactive`.** The flag records an
                            * administrator's statement about what should be
                            * offered going forward — and it is read by nothing
                            * today, so wording it as a live switch would claim an
                            * effect that does not exist. "Inactive" also reads as
                            * a row tidied away, when the row is doing exactly
                            * what it did before.
                            */}
                          {row.isActive ? (
                            <Badge tone="success" dot>
                              Offered
                            </Badge>
                          ) : (
                            <Badge tone="neutral" dot>
                              Not offered
                            </Badge>
                          )}
                        </TD>
                        <TD numeric>{renderActions(row)}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </div>
        )}

        <B2bServiceMapFormModal
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          {...(editing ? { row: editing } : {})}
          onSaved={() => void load()}
        />

        <ConfirmDialog
          open={pendingDelete !== null}
          onClose={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
          onConfirm={() => void confirmDelete()}
          tone="danger"
          title={
            pendingDelete
              ? `Remove the mapping for ${pendingDelete.serviceKey}?`
              : 'Remove mapping?'
          }
          description={
            <>
              {/*
                * ⛔ **PROTECTED — a helper-text sweep must not take this.** It
                * names what is destroyed and, more importantly, **what is not**:
                * a reader will assume deleting a mapping reaches the proposals
                * behind it, because every other master here cascades or
                * restricts. It does not. See `DELETE_CONSEQUENCE`.
                */}
              <p className="m-0">{DELETE_CONSEQUENCE}</p>
              {pendingDelete && (
                <p className="mt-2 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                  <code>{pendingDelete.serviceKey}</code> currently means{' '}
                  {pendingDelete.product.name}.
                </p>
              )}
            </>
          }
          confirmLabel="Remove mapping"
          loading={deleting}
          error={deleteError && <span className="[overflow-wrap:anywhere]">{deleteError}</span>}
        />
      </Panel>
    </div>
  );
}

function MapTableHead() {
  return (
    <THead>
      <TR>
        <TH>Service key</TH>
        <TH>SEED EMERGE product</TH>
        <TH className="w-28">Status</TH>
        <TH className="w-28" numeric>
          Actions
        </TH>
      </TR>
    </THead>
  );
}

/**
 * What a key resolves to, with enough beside it to check.
 *
 * 🔴 **THE NAME COMES FROM THE ROW AND THE MARKERS COME FROM THE CATALOGUE, AND
 * THE TWO FAIL SEPARATELY.** The wire always carries the name, so it always
 * renders; the code and the markers need the catalogue, which may still be
 * arriving or may have failed. Folding the two would mean a failed catalogue read
 * blanking a name the server did give us.
 *
 * ⛔ **THERE IS NO "no longer in the catalogue" STATE HERE, AND ADDING ONE WOULD
 * BE A BRANCH THAT CANNOT FIRE.** `/event-maps` carries one because a project can
 * be deleted out from under a live mapping. Here `product_id` is `NOT NULL` with
 * `ON DELETE RESTRICT` (migration 231, guard 4) — the products module counts
 * `b2bServiceMappings` and refuses the delete — so a mapped product provably
 * still exists, and the catalogue is read unfiltered so it is provably in the
 * list. A branch that cannot fire reads to the next reader as a live rule.
 */
function ProductCell({ row, record }: { row: B2bServiceMap; record: Product | null }) {
  return (
    <div className="min-w-0">
      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{row.product.name}</p>
      <ProductSubtitle record={record} />
    </div>
  );
}

/*
 * ⚠️ **NO `loading` OR `lookupFailed` PARAMETER, WHICH IS A DIVERGENCE FROM
 * `/event-maps` AND NOT AN OVERSIGHT.** That component takes both solely to
 * SUPPRESS its missing-record sentence, because a read that did not happen is not
 * evidence about a record. There is no such sentence here — the record cannot be
 * missing — so the two flags would be props nothing branches on, which reads to
 * the next reader as a rule that is being enforced somewhere.
 */
function ProductSubtitle({ record }: { record: Product | null }) {
  /*
   * ⚠️ **`null` here is ONLY "still arriving" or "the catalogue read failed"**,
   * and the panel's own notice already explains the second. A fixed-height spacer
   * rather than nothing, so a row does not change height as the catalogue lands.
   */
  if (record === null) {
    return <p className="mt-0.5 mb-0 h-4 text-xs text-fg-subtle" aria-hidden="true" />;
  }

  const subtitle = productSubtitle(record);
  return (
    <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
      {subtitle || <span className="text-fg-subtle">Nothing recorded to tell it apart</span>}
      {/*
        * ⚠️ **A retired product still resolves**, because nothing filters on the
        * catalogue's flag any more than on this table's. So this marker is the
        * only place a person can see that a live mapping points at something SEED
        * no longer sells.
        */}
      {!record.productActive && <span className="text-warning"> · retired</span>}
    </p>
  );
}

function TagIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M3.5 12.5V4.5h8l9 9-8 8-9-9Z" />
      <circle cx="8" cy="8" r="1.25" />
    </svg>
  );
}
